/**
 * Basic Markdown → HTML for salon-authored content (the emergency message and
 * treatment descriptions). Deliberately SEPARATE from the chat renderer
 * (helpers/chat/markdown.js), which is specialised for the agent (links, `pick`
 * chips). This one is intentionally basic: paragraphs, line breaks, bold, italic,
 * and bullet/numbered lists — NO links, images, headings, or raw HTML.
 *
 * Pure and config-free: these produce safe HTML strings; the <Markdown> component
 * owns the config toggle, the {@html}, and the styling. Safe by construction — the
 * source is HTML-escaped FIRST, then a fixed set of safe tags is introduced, so the
 * result is safe for {@html}. Link/image syntax degrades to plain text (label kept,
 * target dropped) rather than rendering.
 */

function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/* Inline formatting on already-escaped text. Strip link/image syntax to plain text
   (no <a>, no <img>), then apply bold and italic. */
function inline(escaped) {
    let out = escaped;
    out = out.replace(/!\[[^\]]*\]\([^)]*\)/g, '');       // images → removed entirely
    out = out.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1');     // links → label text only
    out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    out = out.replace(/__([^_]+)__/g, '<strong>$1</strong>');
    out = out.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    out = out.replace(/(^|[^a-zA-Z0-9])_([^_]+)_(?=[^a-zA-Z0-9]|$)/g, '$1<em>$2</em>');
    return out;
}

/* Escaped plain text with newlines as <br> — used when the markdown toggle is off. */
export function renderPlain(source) {
    return escapeHtml(source).replace(/\r\n/g, '\n').replace(/\n/g, '<br>');
}

/* Full basic-Markdown render (escape-first, then safe tags). */
export function renderMarkdown(source) {
    const lines = String(source ?? '').replace(/\r\n/g, '\n').split('\n');
    const html = [];
    let listType = null; // 'ul' | 'ol'
    let paragraph = [];

    const flushParagraph = () => {
        if (paragraph.length) {
            html.push(`<p>${paragraph.map(line => inline(escapeHtml(line))).join('<br>')}</p>`);
            paragraph = [];
        }
    };
    const flushList = () => {
        if (listType) { html.push(`</${listType}>`); listType = null; }
    };

    for (const line of lines) {
        const bullet = line.match(/^\s*[-*]\s+(.*)$/);
        const numbered = line.match(/^\s*\d+\.\s+(.*)$/);

        if (line.trim() === '') { flushParagraph(); flushList(); continue; }

        if (bullet) {
            flushParagraph();
            if (listType !== 'ul') { flushList(); html.push('<ul>'); listType = 'ul'; }
            html.push(`<li>${inline(escapeHtml(bullet[1]))}</li>`);
            continue;
        }
        if (numbered) {
            flushParagraph();
            if (listType !== 'ol') { flushList(); html.push('<ol>'); listType = 'ol'; }
            html.push(`<li>${inline(escapeHtml(numbered[1]))}</li>`);
            continue;
        }

        flushList();
        paragraph.push(line);
    }

    flushParagraph();
    flushList();
    return html.join('');
}
